import * as PIXI from "pixi.js";
import { LanguageManager } from "../manager/LanguageManager";
import { TitanText } from "./TitanText";
import { ControlConstants } from "./constant/ControlConstants";
/**
 * TitanBitmapTextField.ts
 * TextField that uses a PIXI.BitmapText element instead of PIXI.Text
 */
export class TitanBitmapTextField extends TitanText {
    destroy() {
        this.holder.removeChild(this.uiBitmapText);
        this.uiBitmapText?.destroy();
        super.destroy();
    }
    async initialiseUiText() {
        if (this.bitmapActive) {
            return;
        }
        super.initialiseUiText();
    }
    updateBounds() {
        if (this.bitmapActive) {
            const size = this.getSize();
            if (this.checkBounds(size.x, size.y, false)) {
                this.updateHorizontalAlign();
                this.updateVerticalAlign(size);
            }
            else {
                this.resizeFont();
                this.updateBounds();
            }
            this.holder.renderable = true;
        }
        else {
            super.updateBounds();
        }
    }
    updateNoBounds() {
        if (this.bitmapActive) {
            //not required by bitmap fonts
        }
        else {
            super.updateNoBounds();
        }
    }
    //TODO: modify TextField to allow PIXI.BitmapText | PIXI.Text
    getPixiText() {
        if (this.bitmapActive) {
            return this.uiBitmapText;
        }
        return super.getPixiText();
    }
    getMetricsData(respectDirty) {
        if (this.bitmapActive) {
            //TODO: see if we can get more accurate values for ascent, descent
            return {
                width: this.uiBitmapText.width,
                height: this.uiBitmapText.height,
                lines: Math.floor(this.uiBitmapText.height / this.uiBitmapText.maxLineHeight),
                lineHeight: this.uiBitmapText.maxLineHeight,
                ascent: this.uiBitmapText.maxLineHeight,
                descent: this.uiBitmapText.maxLineHeight
            };
        }
        else {
            return super.getMetricsData(respectDirty);
        }
    }
    drawBounds(width, height, exists) {
        if (this.bitmapActive) {
            //Set bounds based on alignment
            let xx;
            let yy;
            let ww = width > 0 ? width : this.uiBitmapText.width;
            let hh = height > 0 ? height : this.uiBitmapText.height;
            switch (this.uiProperties.horizontalAlign) {
                case ControlConstants.CENTER:
                    xx = -ww / 2;
                    break;
                case ControlConstants.RIGHT:
                    xx = -ww;
                    break;
                case ControlConstants.LEFT:
                default:
                    xx = 0;
                    break;
            }
            switch (this.uiProperties.verticalAlign) {
                case ControlConstants.CENTER:
                    yy = -hh / 2;
                    break;
                case ControlConstants.BOTTOM:
                    yy = -hh;
                    break;
                case ControlConstants.TOP:
                default:
                    yy = 0;
                    break;
            }
            if (this.isLocalise || this.isGameDev) {
                if (this.bounds instanceof PIXI.Graphics) {
                    this.bounds.clear();
                }
                else {
                    this.bounds = new PIXI.Graphics();
                }
                // draw bounds
                let valid = exists && this.checkBounds(ww, hh, false);
                let colour = valid ? 0x00FF00 : 0xFF0000;
                this.bounds.beginFill(colour, 1).drawRect(xx, yy, ww, hh).endFill();
                this.holder.addChildAt(this.bounds, 0);
                if (!this.isLocalise && this.isGameDev) {
                    this.bounds.visible = false;
                    this.bounds.alpha = 0.2;
                }
            }
            else {
                this.bounds = new PIXI.Rectangle(xx, yy, ww, hh);
            }
        }
        else {
            super.drawBounds(width, height, exists);
        }
    }
    setText(value, refreshStyle) {
        if (this.bitmapActive) {
            this.updateTextField(value);
            if (this.isLocalise) {
                this.refreshBounds();
            }
        }
        else {
            super.setText(value, refreshStyle);
        }
    }
    getDefaultTextStyle(orientation) {
        if (this.bitmapActive) {
            return null;
        }
        return super.getDefaultTextStyle(orientation);
    }
    crop(resizeFont = false) {
        if (this.bitmapActive) {
            if (resizeFont) {
                this.resizeFont();
            }
            this.refreshBounds(this.uiBitmapText.width, this.uiBitmapText.height);
            this.updateBounds();
        }
        else {
            super.crop(resizeFont);
        }
    }
    setStyle(fontProperties) {
        if (this.bitmapActive) {
            this.updateAlign();
        }
        else {
            super.setStyle(fontProperties);
        }
    }
    getWidth() {
        if (this.bitmapActive) {
            return this.bounds ? this.bounds.width : this.uiBitmapText.width;
        }
        return super.getWidth();
    }
    getHeight() {
        if (this.bitmapActive) {
            return this.bounds ? this.bounds.height : this.uiBitmapText.height;
        }
        return super.getHeight();
    }
    async setAnchorX(x) {
        if (this.bitmapActive) {
            this.uiBitmapText.anchor.x = x;
        }
        else {
            super.setAnchorX(x);
        }
    }
    async setAnchorY(y) {
        if (this.bitmapActive) {
            this.uiBitmapText.anchor.y = y;
        }
        else {
            super.setAnchorY(y);
        }
    }
    static createField(...args) {
        let _parent, _name, _properties, _portraitProperties;
        if (typeof args[1] === "string") {
            [_parent, _name, _properties, _portraitProperties] = args;
        }
        else {
            [_parent, _properties, _portraitProperties] = args;
            _name = "BitmapTextField";
        }
        let field;
        if (_properties) {
            field = new TitanBitmapTextField(_properties, _portraitProperties);
            if (field && _parent) {
                _parent.addChild(field);
            }
        }
        return field;
    }
    clone() {
        const clone = new TitanBitmapTextField(this.landscapeProperties, this.portraitProperties);
        if (this.parent) {
            this.parent.addChild(clone);
        }
        clone.setText(this.getText(), true);
        clone.position.set(this.position.x, this.position.y);
        clone.scale.set(this.scale.x, this.scale.y);
        clone.rotation = this.rotation;
        clone.alpha = this.alpha;
        clone.visible = this.visible;
        return clone;
    }
    _refresh() {
        if (this.bitmapActive) {
            return;
        }
        super._refresh();
    }
    checkBounds(width, height, respectDirty = true) {
        if (this.bitmapActive) {
            const xFit = this.uiBitmapText.textWidth < width;
            const yFit = this.uiBitmapText.textHeight < height;
            return xFit && yFit;
        }
        return super.checkBounds(width, height, respectDirty);
    }
    resizeFont() {
        if (this.bitmapActive) {
            this.uiBitmapText.fontSize--;
        }
        else {
            super.resizeFont();
        }
    }
    createTextFields() {
        if (this.uiProperties.bitmapTextStyle === null || !this.isLocalePermited()) {
            this.bitmapActive = false;
            super.createTextFields();
            return;
        }
        this._initialised = false;
        this._portraitDirty = true;
        this._landscapeDirty = true;
        if (this.uiProperties) {
            this.bitmapActive = true;
            this.uiBitmapText = new PIXI.BitmapText("", {
                fontName: this.uiProperties.bitmapTextStyle.fontName,
                fontSize: this.uiProperties.bitmapTextStyle.fontSize,
                align: this.uiProperties.bitmapTextStyle.align,
                tint: this.uiProperties.bitmapTextStyle.tint,
                letterSpacing: this.uiProperties.bitmapTextStyle.letterSpacing,
                maxWidth: this.uiProperties.bitmapTextStyle.maxWidth
            });
            this.holder.addChild(this.uiBitmapText);
            if (this.uiProperties.langKey) {
                this.setLangKey(this.uiProperties.langKey);
            }
            else {
                this.formattedText = "";
            }
            this.createBounds();
            this.createAccessibility();
        }
    }
    disableBitmap() {
        this.bitmapActive = false;
        this.holder.removeChild(this.uiBitmapText);
        const formattedTextTemp = this.formattedText;
        if (this.uiText) {
            this.holder.addChild(this.uiText);
        }
        else {
            super.createTextFields();
        }
        this.setText(formattedTextTemp, true);
    }
    enableBitmap() {
        if (this.uiBitmapText) {
            this.holder.removeChild(this.uiText);
            this.holder.addChild(this.uiBitmapText);
            this.bitmapActive = true;
            this.updateBounds();
        }
    }
    updateTextField(value) {
        if (this.bitmapActive) {
            this.originalText = value;
            if (!this.locale.isIgnoreCase()) {
                let font = this.uiProperties.fontProperties;
                if (font.uppercase) {
                    value = value.toUpperCase();
                }
                else if (font.lowercase) {
                    value = value.toLowerCase();
                }
            }
            this.formattedText = value;
            this.uiBitmapText.text = this.formattedText;
            this.updateAlign();
        }
        else {
            super.updateTextField(value);
        }
    }
    isLocalePermited() {
        const currentLocale = LanguageManager.getInstance().getLocale().getId();
        if (!this.uiProperties.bitmapTextStyle.allowedLocales) {
            return true;
        }
        return this.uiProperties.bitmapTextStyle.allowedLocales.indexOf(currentLocale) > -1;
    }
    handleRotate(event) {
        super.handleRotate(event);
        if (this.uiBitmapText) {
            const text = this.getText();
            this.holder.removeChild(this.uiBitmapText);
            this.uiBitmapText?.destroy();
            this.createTextFields();
            this.setText(text, true);
        }
    }
}
//# sourceMappingURL=TitanBitmapTextField.js.map